import { IMealPeriod } from "@dining/api-client";
import { createSelector } from "@reduxjs/toolkit";
import { countries, Country } from "countries-list";
import currency from "currency-codes";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { UpdateSyncCafeFormFields } from "../components/CafeSync";
import { UpdateCafeFormFields } from "../components/UpdateCafe/UpdateCafeForm";
import { defaultUpdateCafeFormFields } from "../components/UpdateCafe/updateCafeHelper";
import { RootState } from "../reducers/rootReducer";

dayjs.extend(utc);

export const buildingListSelector = (state: RootState) => state.dining.buildingList;
export const regionListSelector = (state: RootState) => state.dining.regionList;
export const countriesSelector = (state: RootState) => state.dining.countryList;
export const regionSelector = (state: RootState) => state.dining.selectedRegion;
export const syncRegionSelector = (state: RootState) => state.dining.selectedSyncRegion;
export const cafeListSelector = (state: RootState) => state.dining.cafeList;
export const syncCafeListSelector = (state: RootState) => state.dining.syncCafeList;
export const syncCafeAndFlushCache = (state: RootState) => state.dining.syncCafeAndFlushCache;
export const syncAuthorizationFailure = (state: RootState) => state.dining.syncAuthorizationFailure;
export const cafeSelector = (state: RootState) => state.dining.selectedCafe;
export const syncCafeSelector = (state: RootState) => state.dining.selectedSyncCafe;
export const stationListSelector = (state: RootState) => state.dining.stationList;
export const stationSelector = (state: RootState) => state.dining.selectedStation;
export const weekMenuSelector = (state: RootState) => state.dining.weeklyMenu;
export const cafeAllergenBankSelector = (state: RootState) => state.dining.cafeAllergenBank;
export const weekStartSelector = (state: RootState) => state.dining.weekStart ?? dayjs().startOf("week").toDate();
export const selectedItemSelector = (state: RootState) => state.dining.selectedItem;
export const weekStartStringSelector = createSelector(weekStartSelector, (date: string) =>
  dayjs(date).format("YYYY-MM-DD"),
);
export const updateCafeModeSelector = (state: RootState) => state.dining.updateCafeMode;
export const currentModalSelector = (state: RootState) => state.dining.currentModal;
export const panelClosedSelector = (state: RootState) => state.dining.panelClosed;
export const togglePanelSelector = (state: RootState) => state.dining.isAddItemPanelOpen;
export const menuWeekDaySelector = (state: RootState) => state.dining.menuWeekDay;
export const stationItemBankSelector = (state: RootState) => state.dining.stationItemBank;
export const menuItemBankSelector = (state: RootState) => state.dining.menuItemBank;
export const notificationStateSelector = (state: RootState) => state.dining.notificationState;
export const itemDowUpdateFlagSelector = (state: RootState) => state.dining.dowInfoUpdated;
export const editedCafeSelector = createSelector(updateCafeModeSelector, cafeSelector, (mode, selectedCafe) =>
  mode === "Add" || selectedCafe == null
    ? defaultUpdateCafeFormFields
    : ({
        cafeRegion: selectedCafe?.regionId,
        cafeLocation: selectedCafe?.buildingId,
        cafeName: selectedCafe?.name,
        cafeDisclaimer: selectedCafe?.disclaimer,
        cafeCountry: selectedCafe?.countryCode,
        mealPeriods: selectedCafe?.mealPeriods?.map((period) =>
          Object.assign<Object, IMealPeriod, Partial<IMealPeriod>>({}, period, {
            startsOn: dayjs.utc(period.startsOn).format("HH:mm"),
            endsOn: dayjs.utc(period.endsOn).format("HH:mm"),
          }),
        ),
        currencyName: selectedCafe?.currencyName,
        currencySymbol: selectedCafe?.currencySymbol,
        location: selectedCafe?.location,
      } as UpdateCafeFormFields),
);

export const editedSyncCafeSelector = createSelector(syncCafeSelector, (selectedSyncCafe) => {
  return {
    cafeRegion: selectedSyncCafe?.regionId,
    cafeLocation: selectedSyncCafe?.buildingId,
    cafeName: selectedSyncCafe?.name,
    cafeDisclaimer: selectedSyncCafe?.disclaimer,
    cafeCountry: selectedSyncCafe?.countryCode,
    mealPeriods: selectedSyncCafe?.mealPeriods?.map((period) =>
      Object.assign<Object, IMealPeriod, Partial<IMealPeriod>>({}, period, {
        startsOn: dayjs.utc(period.startsOn).format("HH:mm"),
        endsOn: dayjs.utc(period.endsOn).format("HH:mm"),
      }),
    ),
    currencyName: selectedSyncCafe?.currencyName,
    currencySymbol: selectedSyncCafe?.currencySymbol,
    location: selectedSyncCafe?.location,
    disclaimer: selectedSyncCafe?.disclaimer,
  } as UpdateSyncCafeFormFields;
});
const countryDictionary = countries as { [key: string]: Country };
export const currencySelector = createSelector(cafeSelector, (cafe) => {
  const defaultCurrency = {
    code: "USD",
    digits: 0,
    currency: "$",
  };
  const currencySymbol = cafe?.currencySymbol ? { currency: cafe?.currencySymbol } : undefined;
  return Object.assign(
    defaultCurrency,
    currency.code(cafe?.currencyName ?? countryDictionary[cafe?.countryCode ?? "US"].currency.split(",")[0]),
    currencySymbol,
  );
});
